import Api from "../../../services/api";
import { handleApi } from "../../../utils/error";
import * as logger from "../../../utils/logger";
import {
    IInspectionLog,
    IInspectionAsset,
    IInspectedItem,
    IInspectionTemplateDetails,
    IInspectionHistorySection,
    IInspectionSectionData
} from "../store/types";
import { Device } from "../../../model/Device";
import User from "../../../model/User";
import { IApiError, TMultiCallParam } from "../../../utils/api.interfaces";
import { getAssetTypeFromDevice, getInspectionLogTime } from "../utils/assetFormatters";
import { countTemplateItems, calculateInspectionProgress } from "../utils/inspectionProgressCalculation";
import { mapLogTypeToInspectionType, UNKNOWN_ITEM_STATUS } from "../utils/inspectionMappers";
import { isDefectStatus } from "../helpers/severityConfig";

const DEFAULT_RESULTS_LIMIT = 100;

export const DRAFT_STATUS = "Draft";
export const INCOMPLETE_STATUSES = [DRAFT_STATUS];
/**
 * How many recent logs to pull when looking for a recertifiable one. The server has already
 * narrowed by asset, window and inspection type; this only has to cover the case of several
 * different templates being run on one asset inside the same window.
 *
 * The window is capped at InspectionValidator.MaxRecertificationWindowDays so that this scan can
 * actually cover it — see the note there before raising either number.
 */
const RECENT_LOG_SCAN_LIMIT = 20;

/**
 * Days of slack added to the server-side date filter.
 *
 * InspectionLogSearch.FromDate matches on the log's *created* datetime, but recertification is
 * defined in *submitted* time. An inspection started at 23:40 and submitted at 00:20 was created
 * yesterday and completed today, so a filter set to the window start would drop it.
 *
 * The server filter is therefore only a loose prefilter; findRecertifiableInspection applies the
 * real calendar-day rule to the match. A week covers a draft carried across several shifts.
 */
const WINDOW_PREFILTER_BUFFER_DAYS = 7;

export const HISTORY_STATUSES = ["Complete", "Submitted", "InReview", "Reviewed", "ReviewFailed"];

interface IInspectionLogOptions {
    fromDate?: Date;
    toDate?: Date;
    resultsLimit?: number;
    logStatuses?: string[];
    sortBy?: string;
    sortDirection?: "asc" | "desc";
}

interface IExtractedIds {
    deviceIds: string[];
    inspectionTemplateIds: string[];
    userIds: string[];
}

interface ILookupMaps {
    deviceMap: Map<string, Device>;
    inspectionTemplateMap: Map<string, IInspectionTemplateDetails>;
    userMap: Map<string, string>;
}

/**
 * Service for fetching and transforming inspection history logs.
 * Retrieves inspection logs for a user and transforms them to IInspectionAsset format for display.
 */
export class InspectionLogService {
    constructor(private readonly api: Api) {}

    /**
     * Get inspection logs for a specific user (generic method)
     * @param userId User ID to fetch inspection logs for
     * @param options Optional filters: fromDate, toDate, resultsLimit, logStatuses, sortBy, sortDirection
     * @returns Promise resolving to array of InspectionLog entities
     */
    async getInspectionLogs(userId: string, options?: IInspectionLogOptions): Promise<IInspectionLog[]> {
        try {
            const { fromDate, toDate, logStatuses, sortBy, sortDirection = "desc", resultsLimit = DEFAULT_RESULTS_LIMIT } = options ?? {};

            const searchParams = {
                typeName: "InspectionLog",
                search: {
                    createdBySearch: { id: userId },
                    ...(fromDate && { fromDate: fromDate.toISOString() }),
                    ...(toDate && { toDate: toDate.toISOString() }),
                    ...(logStatuses && { logStatuses })
                },
                ...(sortBy && {
                    sort: {
                        sortBy,
                        sortDirection
                    }
                }),
                resultsLimit
            };

            const inspectionLogs = await this.api.callPromise("Get", searchParams);

            return inspectionLogs || [];
        } catch (err) {
            handleApi(err);
            return [];
        }
    }

    /**
     * Get incomplete inspection logs (Draft status) for a user.
     * Sorted by lastEditedDate descending (most recently edited first)
     * @param userId User ID to fetch inspection logs for
     * @param options Optional filters: fromDate, toDate, resultsLimit (logStatuses, sortBy, sortDirection are overridden)
     * @returns Promise resolving to array of incomplete InspectionLog entities
     */
    async getIncompleteInspectionLogs(userId: string, options?: IInspectionLogOptions): Promise<IInspectionLog[]> {
        return this.getInspectionLogs(userId, {
            ...options,
            logStatuses: INCOMPLETE_STATUSES,
            sortBy: "lastEditedDate",
            sortDirection: "desc"
        });
    }

    /**
     * Get history inspection logs (HISTORY_STATUSES) for a user, for the History tab.
     * Sorted by submittedDate descending (most recently submitted first)
     * @param userId User ID to fetch inspection logs for
     * @param options Optional filters: fromDate, toDate, resultsLimit (logStatuses, sortBy, sortDirection are overridden)
     * @returns Promise resolving to array of history InspectionLog entities
     */
    async getHistoryInspectionLogs(userId: string, options?: IInspectionLogOptions): Promise<IInspectionLog[]> {
        return this.getInspectionLogs(userId, {
            ...options,
            logStatuses: HISTORY_STATUSES,
            sortBy: "submittedDate",
            sortDirection: "desc"
        });
    }

    /**
     * Get the most recent completed inspection of a given template on an asset, whoever performed it.
     *
     * Unlike the other lookups here, this is deliberately not scoped to one user (`createdBySearch`):
     * recertification picks up an asset somebody else already inspected.
     *
     * Returns only the most recent match: offering an earlier inspection would carry forward a
     * defect state a later inspector has already dealt with.
     *
     * The template is filtered client-side because InspectionLogSearch has no template-id filter,
     * only `templateName`, which is not a stable identifier.
     *
     * `windowStart` is widened by WINDOW_PREFILTER_BUFFER_DAYS before reaching the server; the
     * caller applies the real window to the match.
     *
     * @param assetId Device/asset ID to search inspections for
     * @param templateId Only inspections performed with this template count
     * @param windowStart Start of the recertification window, before widening
     * @param inspectionTypeId Optional — when set, only inspections of this type count
     */
    async getLastCompletedInspectionForTemplate(
        assetId: string,
        templateId: string,
        windowStart: Date,
        inspectionTypeId?: string
    ): Promise<IInspectionLog | null> {
        try {
            const prefilterFrom = new Date(windowStart);
            prefilterFrom.setDate(prefilterFrom.getDate() - WINDOW_PREFILTER_BUFFER_DAYS);

            const inspectionLogs: IInspectionLog[] = await this.api.callPromise("Get", {
                typeName: "InspectionLog",
                search: {
                    assetSearch: { id: assetId },
                    logStatuses: HISTORY_STATUSES,
                    includeItems: true,
                    fromDate: prefilterFrom.toISOString(),
                    ...(inspectionTypeId ? { inspectionTypeIds: [inspectionTypeId] } : {})
                },
                sort: {
                    sortBy: "submittedDate",
                    sortDirection: "desc"
                },
                resultsLimit: RECENT_LOG_SCAN_LIMIT
            });

            const logs = inspectionLogs ?? [];

            // Pick the latest in memory instead of trusting the server order: `submittedDate` is
            // nullable and sorted NULLS LAST with `gid` as tiebreaker, and a log completed offline
            // can reach a terminal status with no submittedDateTime. Taking the first would then
            // offer an older inspection and silently carry stale defects forward. This also makes
            // the caller's "only the newest needs the window check" shortcut hold by construction.
            let match: IInspectionLog | null = null;
            for (const log of logs) {
                if (log.inspectionTemplate?.id !== templateId) {
                    continue;
                }
                if (!match || getInspectionLogTime(log) > getInspectionLogTime(match)) {
                    match = log;
                }
            }

            // A full page with no match may hide the target just past the limit; the driver would see
            // no offer and no explanation, so log it.
            if (!match && logs.length >= RECENT_LOG_SCAN_LIMIT) {
                logger.log(
                    logger.Source.ERROR,
                    `Recertification scan hit its ${ RECENT_LOG_SCAN_LIMIT }-log limit for asset ${ assetId } without finding template ${ templateId }; an eligible inspection may exist beyond it`
                );
            }

            return match;
        } catch (err) {
            handleApi(err);
            return null;
        }
    }

    /**
     * Transform InspectionLog records to IInspectionAsset format for display
     * Fetches device details, inspection template info, and combines with inspection data
     * @param inspectionLogs - Inspection logs to transform
     * @param assetTypeMap - Map of device IDs to asset type names
     * @param includeProgress - If true, calculates progress for incomplete inspections (requires full template details)
     */
    async transformInspectionLogsToAssets(
        inspectionLogs: IInspectionLog[],
        assetTypeMap: Map<string, string>,
        includeProgress = false
    ): Promise<IInspectionAsset[]> {
        try {
            if (inspectionLogs.length === 0) {
                return [];
            }

            const ids = this.extractUniqueIdsFromLogs(inspectionLogs);
            if (ids.deviceIds.length === 0) {
                return [];
            }

            const multiCalls = this.buildEntityFetchCalls(ids);
            const results = await this.api.multiCallPromise(multiCalls);

            if (!results || results.length === 0) {
                return [];
            }
            const maps = this.createLookupMaps(results, ids);

            return inspectionLogs
                .map(inspectionLog => this.mapInspectionLogToAsset(inspectionLog, maps, assetTypeMap, includeProgress))
                .filter((asset): asset is IInspectionAsset => asset !== null);
        } catch (err) {
            handleApi(err);
            return [];
        }
    }

    /**
     * Fetch admin-repaired defects for a device that still need driver verification.
     * GetInspectedItems with isDefectOnly=true keeps only outstanding repair statuses, so this method
     * calls with isDefectOnly=false and narrows server-side, so the server neither loads nor sends the
     * asset's unrelated history:
     * - repairSources=["Admin"]: only repairs actioned in the MyGeotab web portal need driver
     *   verification. Driver repairs were already certified by a driver, and items with no persisted
     *   origin (older rows) never match rather than being misreported as admin repairs.
     * - itemStatuses=["Repaired"]: excludes items an admin moved on, and items the driver denied
     *   (reopenDeniedRepairedDefect sets "Reported").
     * - repairStatuses=["Repaired"]: the repair record itself must say repaired.
     * - statusChangeReason≠"RepairVerified" stays client-side, as there is no server filter for it:
     *   excludes items the driver already verified. An admin's later repair overwrites the reason,
     *   which puts the item back up for verification.
     * Throws on API error so the caller can handle failure (proceed without the repaired review sheet).
     */
    async getRepairedDefectsForDevice(deviceId: string): Promise<IInspectedItem[]> {
        try {
            const results: IInspectedItem[] = await this.api.callPromise("GetInspectedItems", {
                assetSearch: { id: deviceId },
                isDefectOnly: false,
                itemStatuses: ["Repaired"],
                repairStatuses: ["Repaired"],
                repairSources: ["Admin"]
            });
            // Backend GetInspectedItems deduplicates carry-forward items; kept as a safety net, as in
            // getUnrepairedDefectsForDevice.
            const seen = new Set<string>();
            return (results ?? []).filter(item => {
                if (!item.id || seen.has(item.id)) { return false; }
                // Guards against a server that predates these filters: it silently ignores the parameters and
                // returns the whole history. This happens when the CDN serves a Drive build newer than the
                // server (latest build per major.minor, MYG-118251). Against a current server these checks
                // never reject a row.
                if (item.repairStatus !== "Repaired" || item.repairSource !== "Admin" || item.itemStatus !== "Repaired") { return false; }
                // Currently redundant with the itemStatus filter (verifying sets "Closed"); it becomes the
                // only guard if the server starts keeping verified items "Repaired".
                if (item.statusChangeReason === "RepairVerified") { return false; }
                seen.add(item.id);
                return true;
            });
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /**
     * Fetch unrepaired defects for a device (isDefectOnly = true).
     * Returns InspectedItems enriched with inspectionItemName, inspectionPartName, originalReportDateTime, reportedBy.
     * Throws on API error so the caller can decide how to handle (show badge/sheet or proceed without).
     */
    async getUnrepairedDefectsForDevice(deviceId: string): Promise<IInspectedItem[]> {
        try {
            const results: IInspectedItem[] = await this.api.callPromise("GetInspectedItems", {
                assetSearch: { id: deviceId },
                isDefectOnly: true
            });

            // Backend GetInspectedItems deduplicates carry-forward items.
            // This frontend dedup is kept as a safety net for backwards compatibility.
            const seen = new Set<string>();
            return (results ?? []).filter(item => {
                if (!item.id || seen.has(item.id)) { return false; }
                seen.add(item.id);
                return true;
            });
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /** Batched getUnrepairedDefectsForDevice. Unlike that method, errors yield empty lists instead of throwing. */
    async getUnrepairedDefectsByDeviceIds(deviceIds: string[]): Promise<Map<string, IInspectedItem[]>> {
        if (deviceIds.length === 0) {
            return new Map();
        }
        const calls: TMultiCallParam[] = deviceIds.map(id => [
            "GetInspectedItems",
            { assetSearch: { id }, isDefectOnly: true }
        ]);
        try {
            const results = await this.api.multiCallPromise(calls);
            const defectMap = new Map<string, IInspectedItem[]>();
            deviceIds.forEach((id, i) => {
                const items = (results[i] ?? []) as IInspectedItem[];
                const seen = new Set<string>();
                defectMap.set(id, items.filter(item => {
                    if (!item.id || seen.has(item.id)) { return false; }
                    seen.add(item.id);
                    return true;
                }));
            });
            return defectMap;
        } catch {
            const emptyMap = new Map<string, IInspectedItem[]>();
            deviceIds.forEach(id => emptyMap.set(id, []));
            return emptyMap;
        }
    }

    /**
     * Recent completed inspection logs for one asset, newest first.
     *
     * Sorted in memory rather than trusting the server's order: `submittedDate` is nullable, so
     * InspectionLogBridge sorts it NULLS LAST with `gid` as the tiebreaker, and a log completed
     * offline reaches a terminal status with no submittedDateTime at all — the same trap
     * getLastCompletedInspectionForTemplate documents.
     *
     * The roadside-check redirect needs this to look past non-regulatory inspections instead of
     * taking whichever log happens to be newest. Returns [] on any error.
     */
    async getRecentSubmittedLogsForDevice(assetId: string): Promise<IInspectionLog[]> {
        try {
            const logs: IInspectionLog[] = await this.api.callPromise("Get", {
                typeName: "InspectionLog",
                search: {
                    assetSearch: { id: assetId },
                    logStatuses: HISTORY_STATUSES
                },
                sort: { sortBy: "submittedDate", sortDirection: "desc" },
                resultsLimit: RECENT_LOG_SCAN_LIMIT
            });
            return [...(logs ?? [])].sort((a, b) => getInspectionLogTime(b) - getInspectionLogTime(a));
        } catch {
            return [];
        }
    }

    /**
     * Fetches the most recently submitted inspection log for each device in one batched API call.
     * Returns a Map keyed by device ID. Devices with no completed history are absent from the map.
     * Silently returns an empty map on error so callers can degrade gracefully.
     */
    async getLastSubmittedLogByDeviceIds(deviceIds: string[], userId?: string): Promise<Map<string, IInspectionLog>> {
        if (deviceIds.length === 0) {
            return new Map();
        }
        const calls: TMultiCallParam[] = deviceIds.map(id => [
            "Get",
            {
                typeName: "InspectionLog",
                search: {
                    assetSearch: { id },
                    ...(userId && { createdBySearch: { id: userId } }),
                    logStatuses: HISTORY_STATUSES
                },
                sort: { sortBy: "submittedDate", sortDirection: "desc" },
                resultsLimit: 1
            }
        ]);
        try {
            const results = await this.api.multiCallPromise(calls);
            const logMap = new Map<string, IInspectionLog>();
            deviceIds.forEach((id, i) => {
                const logs = (results[i] ?? []) as IInspectionLog[];
                if (logs[0]) {
                    logMap.set(id, logs[0]);
                }
            });
            return logMap;
        } catch {
            return new Map();
        }
    }

    /**
     * Mark an InspectedItem as repaired by the current user.
     * repairSource="Driver" distinguishes this from admin-portal repairs, so the item never
     * resurfaces in the admin-repaired verification sheet.
     * An InspectedItem carries two status fields, the legacy DVIR repairStatus and the newer
     * itemStatus lifecycle, and the server does not keep them in step. Both are written so the
     * MyGeotab detail page shows a consistent state and InspectedItemBridge records a state-history
     * entry (it only audits when ItemStatus changes). This mirrors the admin portal's repair transition.
     * statusChangeReason "RepairedByDriver" keeps the history distinct from a mechanic's repair. The
     * backend takes a status change's reason and comment from the request only, so the empty comment
     * leaves any earlier admin comment on its own audit entry.
     * Throws on API error so the caller can handle failure (abort navigation, keep sheet open, etc).
     */
    async markDefectAsRepaired(defectId: string, repairById: string, repairNote?: string): Promise<void> {
        try {
            await this.api.callPromise("Set", {
                typeName: "InspectedItem",
                entity: {
                    id: defectId,
                    itemStatus: "Repaired",
                    repairStatus: "Repaired",
                    repairDateTime: new Date().toISOString(),
                    repairBy: { id: repairById },
                    repairSource: "Driver",
                    statusChangeReason: "RepairedByDriver",
                    statusChangeComments: "",
                    ...(repairNote ? { repairNote } : {})
                }
            });
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /**
     * Close an admin-repaired defect's driver review after the driver VERIFIED the repair:
     * itemStatus "Repaired" → "Closed".
     * The "RepairVerified" reason is what excludes the item from the next inspection's review
     * (getRepairedDefectsForDevice), whichever item status the server keeps.
     * repairSource is intentionally omitted: the backend restores omitted nullable fields from the
     * persisted row, so the item keeps repairSource="Admin" and is never re-attributed to the driver.
     * The backend takes a status change's reason and comment from the request only, so the admin's
     * earlier justification stays on its own audit entry.
     * Throws on API error so the caller can handle failure (keep sheet open, etc).
     */
    async closeRepairedDefectReview(defectId: string): Promise<void> {
        try {
            await this.api.callPromise("Set", {
                typeName: "InspectedItem",
                entity: {
                    id: defectId,
                    itemStatus: "Closed",
                    repairStatus: "Repaired",
                    statusChangeReason: "RepairVerified",
                    statusChangeComments: ""
                }
            });
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /**
     * Re-activate an admin-repaired defect the driver DENIED ("Still defective").
     * Called after the new inspection, with the old item linked via the carry-forward path, submits.
     * - itemStatus "Repaired" → "Reported": the driver is no longer asked to verify a repair they
     *   already denied, and the defect returns to the admin portal's Unresolved bucket ("Closed"
     *   would bury the dispute as resolved).
     * - repairStatus → "RepairDisputed": the outstanding-defect query (isDefectOnly=true) matches
     *   NotRepaired and RepairDisputed, so the item reappears in the next unrepaired review, counts
     *   against asset health again. An existing work request is still deduplicated by InspectedItemId, so
     *   none is recreated. Leaving it "Repaired" would strand the defect: no driver would ever see it
     *   again. It is not reverted to NotRepaired because the backend refuses that transition; RepairDisputed also keeps
     *   repairBy/repairDateTime/repairSource as evidence of the claim being rejected.
     * The backend takes a status change's reason and comment from the request only, so the admin's
     * earlier justification stays on its own audit entry.
     * Throws on API error so the caller can decide (best-effort at submission).
     */
    async reopenDeniedRepairedDefect(defectId: string): Promise<void> {
        try {
            await this.api.callPromise("Set", {
                typeName: "InspectedItem",
                entity: {
                    id: defectId,
                    itemStatus: "Reported",
                    repairStatus: "RepairDisputed",
                    statusChangeReason: "DefectStillPresent",
                    statusChangeComments: ""
                }
            });
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /**
     * Link existing InspectedItems (carry-forward defects) to a new InspectionLog.
     * Called after log creation for items the driver marked as "Defect Not Corrected".
     * Returns a result indicating which items succeeded and which failed.
     * With graceful degradation, invalid items are skipped and reported in failedItemIds
     * rather than failing the entire operation. Throws on API error (network, server error, etc).
     */
    async linkExistingInspectedItems(
        inspectionLogId: string,
        inspectedItemIds: string[]
    ): Promise<{ linkedItemIds: string[]; failedItemIds: string[]; allSucceeded: boolean }> {
        if (inspectedItemIds.length === 0) {
            return { linkedItemIds: [], failedItemIds: [], allSucceeded: true };
        }
        try {
            const result = await this.api.callPromise("LinkExistingInspectedItems", {
                inspectionLogId,
                inspectedItemIds
            });
            return {
                linkedItemIds: result?.linkedItemIds ?? [],
                failedItemIds: result?.failedItemIds ?? [],
                allSucceeded: result?.allSucceeded ?? true
            };
        } catch (err) {
            handleApi(err);
            throw err;
        }
    }

    /**
     * Permanently delete a Draft InspectionLog by ID.
     * The backend cascades the delete to all associated InspectedItems in a transaction.
     * Throws on API error so the caller can surface a toast and keep the UI intact.
     */
    async deleteInspectionLog(inspectionLogId: string): Promise<void> {
        await this.api.callPromise("Remove", {
            typeName: "InspectionLog",
            entity: { id: inspectionLogId }
        });
    }

    /**
     * Fetch a single User by ID (inspector's license/carrier and certifier's display name).
     * Returns null on any failure: the lookup is purely additive, so callers render without it.
     */
    async getUserById(userId: string): Promise<IUser | null> {
        try {
            const results = await this.api.callPromise("Get", {
                typeName: "User",
                search: { id: userId },
                resultsLimit: 1
            });
            return results?.[0] ?? null;
        } catch (err) {
            logger.error(logger.Source.ERROR, `getUserById failed for roadside view (user ${ userId })`, undefined, err as IApiError);
            return null;
        }
    }

    /**
     * Names of the companion assets (e.g. the trailer) inspected in the same session as the
     * given log. Mirrors DVIR's "Trailers" row. Failures are logged and return [].
     */
    async getCompanionAssetNames(inspectionSessionId: string, excludeLogId: string): Promise<string[]> {
        try {
            const logs: IInspectionLog[] = await this.api.callPromise("Get", {
                typeName: "InspectionLog",
                search: { inspectionSessionId }
            });
            const companionAssetIds = Array.from(new Set((logs ?? [])
                .map(log => (log.id !== excludeLogId ? log.asset?.id : undefined))
                .filter((id): id is string => !!id)));
            if (companionAssetIds.length === 0) {
                return [];
            }
            const devices: Device[] = await this.api.callPromise("Get", {
                typeName: "Device",
                search: { deviceIds: companionAssetIds }
            });
            return (devices ?? []).map(device => device.name).filter(Boolean);
        } catch (err) {
            logger.error(logger.Source.ERROR, `getCompanionAssetNames failed for session ${ inspectionSessionId }`, undefined, err as IApiError);
            return [];
        }
    }

    /**
     * Get a single inspection log by ID with all inspectedItems
     */
    async getInspectionLogById(inspectionLogId: string): Promise<IInspectionLog | null> {
        try {
            const results = await this.api.callPromise("Get", {
                typeName: "InspectionLog",
                search: { id: inspectionLogId }
            });
            return results?.[0] ?? null;
        } catch (err) {
            handleApi(err);
            return null;
        }
    }

    /**
     * Transform inspectedItems from a Draft InspectionLog back into sectionDataMap format
     * for resuming an incomplete inspection. Maps each inspected item back to its section
     * (inspection part) and populates the in-memory form state.
     * @param inspectedItems Items saved in the Draft log (from backend)
     * @param templateDetails Template structure to map items to sections
     * @returns sectionDataMap suitable for pre-populating the inspection form
     *
     * A section gets an entry as soon as any saved item maps to it, including Unknown-status
     * placeholders. Sections with no saved item are intentionally absent, so `isVisited`
     * (`part.id in sectionDataMap` in inspectionDetails.tsx) is false for them on resume.
     */
    loadDraftSectionData(inspectedItems: IInspectedItem[], templateDetails: IInspectionTemplateDetails): Record<string, IInspectionSectionData> {
        const sectionDataMap: Record<string, IInspectionSectionData> = {};

        const itemToPartMap = new Map<string, string>();
        for (const part of templateDetails.inspectionParts ?? []) {
            for (const item of part.inspectionItems ?? []) {
                itemToPartMap.set(item.id, part.id);
            }
        }

        // Sections with any recorded non-Pass item (Reported, InProgress, Monitoring, legacy Fail, ...)
        // cannot be inferred as noDefects. Unknown never reaches this set; it is skipped in the loop
        // and handled by allItemsPresent. In a draft, Pass means "confirmed passing": saved either
        // via section noDefects=true or by the driver tapping ✓ on an item.
        const sectionsWithNonPassItem = new Set<string>();

        for (const inspectedItem of inspectedItems) {
            const templateItemId = inspectedItem.inspectionItem?.id;
            if (!templateItemId) {
                continue;
            }

            const partId = itemToPartMap.get(templateItemId);
            if (!partId) {
                continue;
            }

            if (!sectionDataMap[partId]) {
                sectionDataMap[partId] = { noDefects: false, signature: "", items: {} };
            }

            // Unknown status means the item was not yet evaluated — leave it absent so it
            // shows as unselected when the driver reopens the draft.
            if (inspectedItem.itemStatus === UNKNOWN_ITEM_STATUS) {
                continue;
            }

            if (inspectedItem.itemStatus !== "Pass") {
                sectionsWithNonPassItem.add(partId);
            }

            const hasDefect = isDefectStatus(inspectedItem.itemStatus);
            sectionDataMap[partId].items[templateItemId] = {
                hasDefect,
                notes: inspectedItem.itemNote ?? "",
                severity: inspectedItem.itemSeverity?.toLowerCase() ?? "",
                signature: "",
                value: inspectedItem.itemValue ?? "",
                // Restore uploaded photo IDs so they're not re-uploaded on resume.
                images: inspectedItem.mediaFiles?.map(mf => mf.id) ?? []
            };
        }

        // Infer noDefects only when every template item came back as Pass; a missing or Unknown item
        // means the section was never confirmed.
        for (const part of templateDetails.inspectionParts ?? []) {
            const sectionData = sectionDataMap[part.id];
            if (!sectionData) {
                continue;
            }
            // Not covered by unit tests: a section only exists when an item of this part was saved, so the part always has items and the empty fallback is dead. This code requires rework the next time it is changed.
            const allItems = part.inspectionItems ?? [];
            if (allItems.length === 0) {
                continue;
            }
            const hasAnyDefect = allItems.some(item => sectionData.items[item.id]?.hasDefect);
            const allItemsPresent = allItems.every(item => sectionData.items[item.id] !== undefined);
            if (allItemsPresent && !hasAnyDefect && !sectionsWithNonPassItem.has(part.id)) {
                sectionData.noDefects = true;
            }
        }

        return sectionDataMap;
    }

    /**
     * Extract unique device, inspection template, and reviewer user IDs from inspection logs.
     * The submitter is not collected: submittedBy is embedded in the log.
     */
    private extractUniqueIdsFromLogs(inspectionLogs: IInspectionLog[]): IExtractedIds {
        const deviceIds = new Set<string>();
        const inspectionTemplateIds = new Set<string>();
        const userIds = new Set<string>();

        for (const inspectionLog of inspectionLogs) {
            if (inspectionLog.asset?.id) {
                deviceIds.add(inspectionLog.asset.id);
            }
            if (inspectionLog.inspectionTemplate?.id) {
                inspectionTemplateIds.add(inspectionLog.inspectionTemplate.id);
            }
            if (inspectionLog.reviewedBy?.id) {
                userIds.add(inspectionLog.reviewedBy.id);
            }
        }

        return {
            deviceIds: Array.from(deviceIds),
            inspectionTemplateIds: Array.from(inspectionTemplateIds),
            userIds: Array.from(userIds)
        };
    }

    /**
     * Build multiCall parameters to fetch devices, inspection templates, and users
     * - Devices: single batch call with deviceIds array (API supports this)
     * - Inspection templates/Users: individual calls (no batch support)
     * Order: [1 device batch], [N inspection template calls], [M user calls].
     * createLookupMaps parses results positionally, so the two must stay in sync.
     */
    private buildEntityFetchCalls(ids: IExtractedIds): TMultiCallParam[] {
        const calls: TMultiCallParam[] = [];

        if (ids.deviceIds.length > 0) {
            calls.push(["Get", { typeName: "Device", search: { deviceIds: ids.deviceIds } }]);
        }

        for (const id of ids.inspectionTemplateIds) {
            calls.push(["Get", { typeName: "InspectionTemplate", search: { id } }]);
        }

        for (const id of ids.userIds) {
            calls.push(["Get", { typeName: "User", search: { id } }]);
        }

        return calls;
    }

    /**
     * Parse multiCall results (laid out by buildEntityFetchCalls) into lookup maps.
     */
    private createLookupMaps(results: unknown[], ids: IExtractedIds): ILookupMaps {
        const deviceMap = new Map<string, Device>();
        const inspectionTemplateMap = new Map<string, IInspectionTemplateDetails>();
        const userMap = new Map<string, string>();

        let index = 0;

        if (ids.deviceIds.length > 0) {
            const deviceResults = (results[index++] || []) as Device[];
            for (const device of deviceResults) {
                deviceMap.set(device.id, device);
            }
        }

        for (let i = 0; i < ids.inspectionTemplateIds.length; i++) {
            const inspectionTemplateArray = (results[index++] || []) as IInspectionTemplateDetails[];
            if (inspectionTemplateArray[0]) {
                const inspectionTemplate = inspectionTemplateArray[0];
                inspectionTemplateMap.set(inspectionTemplate.id, inspectionTemplate);
            }
        }

        for (let i = 0; i < ids.userIds.length; i++) {
            const userArray = (results[index++] || []) as IUser[];
            if (userArray[0]) {
                const user = userArray[0];
                userMap.set(user.id, new User(user).getFullName());
            }
        }

        return { deviceMap, inspectionTemplateMap, userMap };
    }

    /**
     * Transform a single InspectionLog to IInspectionAsset
     * @param inspectionLog - Inspection log to transform
     * @param maps - Lookup maps for devices, templates, and users
     * @param assetTypeMap - Map of device IDs to asset type names
     * @param includeProgress - If true, calculates progress for incomplete inspections
     * @returns IInspectionAsset or null if required data is missing
     */
    private mapInspectionLogToAsset(
        inspectionLog: IInspectionLog,
        maps: ILookupMaps,
        assetTypeMap: Map<string, string>,
        includeProgress = false
    ): IInspectionAsset | null {
        if (!inspectionLog.asset?.id) {
            return null;
        }

        const device = maps.deviceMap.get(inspectionLog.asset.id);
        if (!device) {
            return null;
        }

        const assetType = getAssetTypeFromDevice(device, assetTypeMap);
        if (!assetType) {
            return null;
        }

        const inspectionTemplate = inspectionLog.inspectionTemplate?.id
            ? maps.inspectionTemplateMap.get(inspectionLog.inspectionTemplate.id)
            : undefined;

        const reviewedByName = inspectionLog.reviewedBy?.id ? maps.userMap.get(inspectionLog.reviewedBy.id) : undefined;
        const submittedByName = inspectionLog.submittedBy ? new User(inspectionLog.submittedBy).getFullName() : undefined;

        // For Draft logs, Unknown-status items are unevaluated placeholders and must not count toward progress.
        const completedCount = inspectionLog.logStatus === DRAFT_STATUS
            ? (inspectionLog.inspectedItems?.filter(i => i.itemStatus !== UNKNOWN_ITEM_STATUS).length ?? 0)
            : (inspectionLog.inspectedItems?.length ?? 0);
        const inspectionProgress =
            includeProgress && inspectionTemplate
                ? calculateInspectionProgress(completedCount, countTemplateItems(inspectionTemplate.inspectionParts ?? []))
                : undefined;

        const inspectionType = inspectionLog.logType ? mapLogTypeToInspectionType(inspectionLog.logType) : undefined;

        return {
            device,
            assetType: assetType.name,
            assetTypeId: assetType.id,
            inspectionLog,
            inspectionTemplate,
            reviewedByName,
            submittedByName,
            inspectionProgress,
            inspectionType
        };
    }
}

/**
 * Groups flat inspectedItems from a log with template structure.
 * Matches each inspected item to its template item (by inspectionItem.id)
 * and groups them by template part (section). Items whose template item is gone are dropped.
 *
 * TODO: temporary client-side grouping; remove once the API returns inspected items grouped by section.
 *
 * @param inspectedItems - Flat list of inspected items from the inspection log
 * @param templateDetails - Template with parts and items structure
 * @returns Array of sections with their matched inspected items, sorted by sequence
 */
export function groupInspectedItemsBySection(
    inspectedItems: IInspectedItem[],
    templateDetails: IInspectionTemplateDetails
): IInspectionHistorySection[] {
    if (inspectedItems.length === 0) {
        return [];
    }

    const parts = templateDetails.inspectionParts ?? [];

    // Build lookup: template item id → its section, display name, sequence and regulation state.
    const templateItemLookup = new Map<
        string,
        {
            partId: string;
            partName: string;
            partSequence: number;
            itemName: string;
            itemSequence: number;
            isRegulated?: boolean;
        }
    >();

    for (const part of parts) {
        for (const item of part.inspectionItems ?? []) {
            templateItemLookup.set(item.id, {
                partId: part.id,
                partName: part.name,
                partSequence: part.sequence ?? 0,
                itemName: item.name,
                itemSequence: item.sequence ?? 0,
                isRegulated: item.isRegulated
            });
        }
    }

    const sectionMap = new Map<string, IInspectionHistorySection>();

    for (const inspectedItem of inspectedItems) {
        const templateItemId = inspectedItem.inspectionItem?.id;
        if (!templateItemId) {
            continue;
        }

        const templateItemInfo = templateItemLookup.get(templateItemId);
        if (!templateItemInfo) {
            continue;
        }

        let section = sectionMap.get(templateItemInfo.partId);
        if (!section) {
            section = {
                id: templateItemInfo.partId,
                name: templateItemInfo.partName,
                sequence: templateItemInfo.partSequence,
                inspectedItems: []
            };
            sectionMap.set(templateItemInfo.partId, section);
        }

        section.inspectedItems.push({
            ...inspectedItem,
            itemName: templateItemInfo.itemName,
            itemSequence: templateItemInfo.itemSequence,
            isRegulated: templateItemInfo.isRegulated
        });
    }

    const sections = Array.from(sectionMap.values());

    for (const section of sections) {
        section.inspectedItems.sort((a, b) => a.itemSequence - b.itemSequence);
    }

    // Not covered by unit tests: every section is created with a numeric sequence, so the ?? 0 fallbacks are dead. This code requires rework the next time it is changed.
    return sections.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}
